import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { SubtitleStore } from '../state/subtitleStore.ts';
import type { UiLocale } from '../i18n/strings.ts';
import { getUiLocale } from '../i18n/index.ts';

/**
 * Pick a default *learning* explanation language (Azure GPT codes).
 * Soft preference only — Settings still allows empty until the user picks one.
 */
export function resolveDefaultLearningTargetLang(options?: {
  uiLocale?: UiLocale;
  navigatorLanguage?: string;
}): string {
  const ui = options?.uiLocale ?? getUiLocale();
  const nav = (
    options?.navigatorLanguage
    ?? (typeof navigator !== 'undefined' ? navigator.language : '')
  ).toLowerCase();

  if (ui === 'zh-CN') return 'zh-Hans';
  if (ui === 'zh-TW') return 'zh-Hant';
  if (ui === 'ja') return 'ja';
  if (ui === 'ko') return 'ko';
  if (
    nav === 'zh-cn' ||
    nav === 'zh-sg' ||
    nav.startsWith('zh-hans') ||
    nav === 'zh'
  ) {
    return 'zh-Hans';
  }
  if (nav.startsWith('zh')) return 'zh-Hant';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('de')) return 'de';
  if (nav.startsWith('pt')) return 'pt';
  if (nav.startsWith('vi')) return 'vi';
  if (nav.startsWith('th')) return 'th';
  if (nav.startsWith('id')) return 'id';
  if (nav.startsWith('en')) return 'en';
  return 'zh-Hant';
}

/**
 * Pick a default dual-subtitle / Google-style translation language (BCP-47 region).
 * Soft preference for Tracks / UI hints — never written into `mainTranslations`
 * automatically (translation stays off until the user picks one).
 */
export function resolveDefaultTargetLang(options?: {
  uiLocale?: UiLocale;
  navigatorLanguage?: string;
}): string {
  const ui = options?.uiLocale ?? getUiLocale();
  const nav = (
    options?.navigatorLanguage
    ?? (typeof navigator !== 'undefined' ? navigator.language : '')
  ).toLowerCase();

  if (ui === 'zh-CN') return 'zh-CN';
  if (ui === 'zh-TW') return 'zh-TW';
  if (ui === 'ja') return 'ja';
  if (ui === 'ko') return 'ko';
  if (
    nav === 'zh-cn' ||
    nav === 'zh-sg' ||
    nav.startsWith('zh-hans') ||
    nav === 'zh'
  ) {
    return 'zh-CN';
  }
  if (nav.startsWith('zh')) return 'zh-TW';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('fr')) return 'fr';
  if (nav.startsWith('de')) return 'de';
  if (nav.startsWith('pt')) return 'pt';
  if (nav.startsWith('vi')) return 'vi';
  if (nav.startsWith('th')) return 'th';
  if (nav.startsWith('id')) return 'id';
  if (nav.startsWith('en')) return 'en';
  return 'zh-TW';
}

export function translationCodeAvailable(
  code: string | undefined,
  translationCodes: string[] | undefined,
): boolean {
  if (!code || code === 'null') return false;
  if (!translationCodes || translationCodes.length === 0) return Boolean(code);
  const lower = code.toLowerCase();
  return translationCodes.some((c) => c.toLowerCase() === lower);
}

/**
 * After store.reset() / track list load: show slots; dualMode stays off until the user
 * toggles it (extension dualMain/dualSecond runtime defaults — not tied to translation MRU).
 */
export function applyDualViewFromSettings(
  store: SubtitleStore,
  _settings?: SettingsStore | unknown,
  _options?: { translationCodes?: string[] },
): void {
  store.setViewState('main', {
    hidden: false,
    dualMode: false,
  });
  store.setViewState('second', {
    hidden: false,
    dualMode: false,
  });
}

/** Drop in-place translations so source-only view has no leftover dual paint. */
export function clearSlotTranslations(store: SubtitleStore, slot: 'main' | 'second'): void {
  const cues = store.getSlot(slot).cues;
  if (!cues?.length) return;
  for (const cue of cues) {
    cue.translated = false;
    cue.translation = undefined;
  }
}
